import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';

import { LoadingIndicator } from '@/components/base/loading-indicator/loading-indicator';
import { orgFeatureFlagQueryOptions } from '@/lib/feature-flags/client/org-feature-flag-query-options';

import { previewRecordingPanelEnabled } from '../-preview/preview-recording-panel';
import { projectToPreviewProps } from '../-preview/project-to-preview-props';
import { ProjectPreview } from '../-project-preview';
import { researcherProjectQueryOptions } from './-shared';

export const Route = createFileRoute(
  '/researchers/_authenticated/projects/$projectId/preview',
)({
  // Render this page on the client only. It opens in a brand-new tab
  // (target="_blank"), so it pays a full cold SSR+hydration cost; server
  // rendering the interactive walkthrough would paint an "Apply" button that
  // isn't wired up until hydration finishes, so an early click is silently
  // lost. Ancestor beforeLoads (auth, team, project prefetch) still run on the
  // server, so auth stays enforced and the project query stays warm.
  ssr: false,
  head: () => ({ meta: [{ title: t`Preview` }] }),
  component: ProjectPreviewPage,
});

function ProjectPreviewPage() {
  const { projectId } = Route.useParams();
  const { user } = Route.useRouteContext();

  const projectQuery = useQuery(
    researcherProjectQueryOptions(projectId, user.id),
  );

  const result = projectQuery.data;
  const project = result?.success ? result.data : null;

  // Recording panel layout parity with the participant page: both org
  // rollouts, read for the project's organization.
  const organizationId = project?.organizationId;
  const { data: usabilityTestingEnabled = false } = useQuery(
    orgFeatureFlagQueryOptions(organizationId, 'usability-testing'),
  );
  const { data: recordingPanelFlag = false } = useQuery(
    orgFeatureFlagQueryOptions(organizationId, 'recording-panel'),
  );

  // Reflect the previewed project in the browser tab title.
  useEffect(() => {
    if (project) {
      document.title = t`Preview · ${project.name}`;
    }
  }, [project]);

  if (projectQuery.isPending) {
    return (
      <div className="flex h-screen items-center justify-center bg-secondary">
        <LoadingIndicator size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 bg-secondary px-6 text-center">
        <p className="text-lg font-semibold text-primary">
          <Trans>Couldn&apos;t load preview</Trans>
        </p>
        <p className="text-sm text-tertiary">
          <Trans>
            We couldn&apos;t load this project. Try refreshing the page.
          </Trans>
        </p>
      </div>
    );
  }

  const previewProps = projectToPreviewProps(project);
  const recordingPanel = previewRecordingPanelEnabled({
    usabilityTesting: usabilityTestingEnabled,
    recordingPanelFlag,
    interviewDeliveryMode: previewProps.interviewDeliveryMode,
    interviewSections: previewProps.interviewSections,
  });

  return (
    <div className="flex h-screen flex-col bg-secondary">
      <ProjectPreview
        {...previewProps}
        recordingPanel={recordingPanel}
        projectId={projectId}
        variant="page"
        title={project.name}
        onRefresh={() => projectQuery.refetch()}
      />
    </div>
  );
}
